import React, { useState } from 'react';
import PropTypes from 'prop-types';

export default function ProductCard({ product }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Handle image loading errors
  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Safe link handling with fallback
  const handleLinkClick = (e, link, platform) => {
    if (!link || link === '#') {
      e.preventDefault();
      console.warn(`${platform} link not available for product: ${product.title}`);
      return;
    }
    
    // Optional: Add analytics tracking
    // trackClick(platform, product.id);
  };

  // Format price safely
  const formatPrice = (price) => {
    if (!price || price === 'N/A') return 'Price not available';
    
    try {
      // Remove currency symbols and parse
      const cleanPrice = price.toString().replace(/[€$£,]/g, '');
      const numPrice = parseFloat(cleanPrice);
      
      if (isNaN(numPrice)) return price; // Return original if can't parse
      
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: 'EUR' 
      }).format(numPrice);
    } catch (error) {
      return price; // Return original price if formatting fails
    }
  };

  // Generate star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={`full-${i}`} className="w-4 h-4 text-[#b59f6f]" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    if (hasHalfStar) {
      stars.push(
        <svg key="half" className="w-4 h-4 text-[#b59f6f]" fill="currentColor" viewBox="0 0 20 20">
          <defs>
            <linearGradient id="half-star">
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="#e5e7eb" />
            </linearGradient>
          </defs>
          <path fill="url(#half-star)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-4 h-4 text-[#ddd7cf]" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    return stars;
  };

  // Fallback image URL
  const fallbackImage = '/default-product-image.jpg';
  const imageUrl = imageError 
    ? fallbackImage 
    : (product.image || fallbackImage);

  return (
    <article className="product-card bg-white/80 border border-[#8C7B6B]/20 rounded-none shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md focus-within:ring-2 focus-within:ring-[#8C7B6B]">
      {/* Product Image */}
      <div className="relative overflow-hidden">
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#8C7B6B]/30 border-t-[#CC5833] rounded-full animate-spin"></div>
          </div>
        )}
        
        <a 
          href={product.affiliateLink}
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => handleLinkClick(e, product.affiliateLink, 'Affiliate')}
          className="block focus:outline-none focus:ring-2 focus:ring-[#8C7B6B] min-h-[44px] min-w-[44px]"
          aria-label={`View ${product.title} on affiliate site`}
        >
          <img
            src={imageUrl}
            alt={product.title || 'Product image'}
            className={`w-full h-48 object-cover image-editorial ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading="lazy"
          />
        </a>
        
        {/* Sale badge */}
        {product.onSale && (
          <span className="absolute top-2 left-2 bg-[#2E4036] text-[#F2F0E9] text-[0.65rem] font-semibold px-2 py-1 tracking-[0.2em] uppercase">
            SALE
          </span>
        )}
        
        {/* Category badge */}
        {product.category && product.category !== 'uncategorized' && (
          <span className={`absolute top-2 ${product.onSale ? 'right-2' : 'left-2'} bg-[#E8E3D9] text-[#2E4036] text-[0.65rem] font-semibold px-2 py-1 tracking-[0.16em] uppercase`}>
            {product.category}
          </span>
        )}
      </div>

      {/* Product Details */}
      <div className="p-5">
        <h3 className="font-display text-xl mb-2 line-clamp-2 text-[#2E4036] hover:text-[#1a2a20] transition-colors">
          {product.title || 'Product Title Not Available'}
        </h3>
        
        {product.excerpt && (
          <p className="text-sm text-[#4A4541] mb-4 line-clamp-3">
            {product.excerpt}
          </p>
        )}

        {/* Price and Rating */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-[#2E4036]">
              {formatPrice(product.price)}
            </span>
            {product.onSale && product.regularPrice && (
              <span className="text-sm text-[#8C7B6B] line-through">
                {formatPrice(product.regularPrice)}
              </span>
            )}
          </div>
          
          {product.rating > 0 && (
            <div className="flex items-center">
              <div className="flex mr-1">
                {renderStars(product.rating)}
              </div>
              <span className="text-sm text-[#8C7B6B]">({product.rating})</span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <a
          href={product.affiliateLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => handleLinkClick(e, product.affiliateLink, 'Affiliate')}
          className="w-full bg-[#2E4036] text-[#F2F0E9] py-3 px-4 uppercase tracking-[0.2em] text-xs hover:bg-[#1a2a20] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#8C7B6B] focus:ring-offset-2 text-center block"
        >
          Check Price
        </a>
      </div>
    </article>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    excerpt: PropTypes.string,
    image: PropTypes.string,
    price: PropTypes.string,
    regularPrice: PropTypes.string,
    salePrice: PropTypes.string,
    affiliateLink: PropTypes.string,
    category: PropTypes.string,
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    onSale: PropTypes.bool,
    featured: PropTypes.bool,
    date: PropTypes.string
  }).isRequired
};
